// Library Methods
import { useCallback, useMemo } from "react";

// Styles
import styles from "./patternDevGraph.module.css";

// Components
import Skeleton from "@mui/material/Skeleton";
import Dash from "src/assets/shapes/Dash/Dash";
import ColoredCircle from "src/assets/shapes/ColoredCircle/ColoredCircle";

// Utils
import { getDeviationChartPattern } from "src/utils/exams/examReportsHelper";
import { getVisualFieldGrid } from "src/utils/exams/numberChartHelper";

// Constants
import { eyeTypeEnums, gridTypeEnums } from "src/constants/enums";

// Types
import { ChartSpotType } from "src/services/exams/types";

const DEFAULT_CONTAINER_SIZE = 252;

type PatternDevGraphType = {
  pressedPoints: Array<ChartSpotType>;
  eyeType: string;
  gridType: string | undefined;
  isLiveUpdate?: boolean;
  containerSize?: number;
};

const PatternDevGraph = ({
  pressedPoints,
  eyeType,
  gridType,
  isLiveUpdate = false,
  containerSize = DEFAULT_CONTAINER_SIZE,
}: PatternDevGraphType) => {
  const { LEFT, RIGHT } = eyeTypeEnums;

  const size = useMemo(() => containerSize * 0.9, [containerSize]);

  const scaleFactor = useMemo(
    () => containerSize / DEFAULT_CONTAINER_SIZE,
    [containerSize]
  );

  const getImage = (valuePercentile: number | undefined) => {
    const pattern = getDeviationChartPattern(valuePercentile);
    if (!pattern)
      return (
        <div
          style={{
            width: 12,
            height: 6,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontSize: "16px",
          }}
        >
          .
        </div>
      );

    return (
      <img
        src={pattern}
        alt="Total deviation value representation"
        className={styles.imageRender}
        style={{ width: 12, height: 12, objectFit: "fill" }}
      />
    );
  };

  const allExamPoints = useMemo(
    () => getVisualFieldGrid(gridType, eyeType),
    [gridType, eyeType]
  );

  const shouldIgnoreValue = useCallback(
    (spot: { x: number; y: number }) => {
      return (
        gridType !== gridTypeEnums.G_10_2.type &&
        ((eyeType === LEFT && spot.x === -3 && spot.y === -1) ||
          (eyeType === LEFT && spot.x === -3 && spot.y === 1) ||
          (eyeType === RIGHT && spot.x === 3 && spot.y === -1) ||
          (eyeType === RIGHT && spot.x === 3 && spot.y === 1))
      );
    },
    [eyeType, gridType, LEFT, RIGHT]
  );

  const calculateXPaddingPatternGraph = useCallback((xFloor: number) => {
    // jumpAxis lets us jump from negative x axis to positive x axis, 152 (half of total width + line width + 5px padding)
    const jumpAxis = 105;

    if (xFloor <= 0) {
      return 122 - xFloor * -23;
    } else {
      return xFloor * 23 + jumpAxis;
    }
  }, []);

  const calculateYPaddingPatternGraph = useCallback((yFloor: number) => {
    // jumpAxis lets us jump from positive y axis to negative y axis, 152 (half of total height + line width + 5px padding)
    const jumpAxis = 100;

    if (yFloor >= 0) {
      return 118 - yFloor * 25;
    } else {
      return jumpAxis + yFloor * -25;
    }
  }, []);

  const getPaddingLeft = useCallback(
    (ml: number) => (ml - 4) * scaleFactor,
    [scaleFactor]
  );

  const getPaddingTop = useCallback(
    (mt: number) => mt * scaleFactor,
    [scaleFactor]
  );

  // Show loader while data is not yet processed
  if (pressedPoints.length === 0) {
    return (
      <Skeleton
        variant="rounded"
        width="100%"
        height={DEFAULT_CONTAINER_SIZE}
        sx={{
          borderRadius: "4px",
        }}
      />
    );
  }

  return (
    <main
      style={{
        height: `${size}px`,
        width: `${size}px`,
        position: "relative",
      }}
    >
      {/* Vertical Line */}
      <div
        style={{
          position: "absolute",
          borderRight: "2px solid #000",
          height: `${size}px`,
          marginLeft: `${size / 2}px`,
        }}
      />

      {/* Horizontal Line */}
      <div
        style={{
          position: "absolute",
          borderBottom: "2px solid #000",
          width: `${size}px`,
          marginTop: `${size / 2}px`,
        }}
      />

      {allExamPoints.map((spot, i) => {
        const hasValue = pressedPoints
          .filter((point) => point.decibelValue !== null) // disconsider the points that has a decibelValue equal to null
          .find((point) => point.x === spot.x && point.y === spot.y);

        const ml = calculateXPaddingPatternGraph(spot.x);
        const mt = calculateYPaddingPatternGraph(spot.y);

        if (hasValue) return null;

        return (
          <div
            key={i}
            style={{
              position: "absolute",
              paddingLeft: getPaddingLeft(ml),
              paddingTop: getPaddingTop(mt),
            }}
          >
            <Dash size={12} />
          </div>
        );
      })}

      {pressedPoints
        .filter((spot) => !spot.completed && spot.decibelValue !== null) // considering the non completed ones, with a valid decibelValue/intensity
        .map((spot, i) => {
          const ml = calculateXPaddingPatternGraph(spot.x);
          const mt = calculateYPaddingPatternGraph(spot.y);

          return (
            <div
              key={i}
              style={{
                position: "absolute",
                paddingLeft: getPaddingLeft(ml),
                paddingTop: getPaddingTop(mt),
              }}
            >
              {!isLiveUpdate ? (
                <Dash size={12} />
              ) : (
                <ColoredCircle size={12} isOrange={!spot.current} />
              )}
            </div>
          );
        })}

      {/* Completed spots */}
      {pressedPoints
        .filter((spot) => spot.completed)
        .map((spot, i) => {
          // check if it's a point that should be ignored
          const shouldIgnore = shouldIgnoreValue(spot);

          const ml = calculateXPaddingPatternGraph(spot.x);
          const mt = calculateYPaddingPatternGraph(spot.y);

          if (shouldIgnore) return null;

          return (
            <div
              key={i}
              style={{
                position: "absolute",
                paddingLeft: getPaddingLeft(ml),
                paddingTop: getPaddingTop(mt),
              }}
            >
              {getImage(spot.valuePercentile)}
            </div>
          );
        })}
    </main>
  );
};

export default PatternDevGraph;
